import { Button, ButtonWithLoader } from '@neo/primitives/button'
import { Checkbox } from '@neo/primitives/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { useEffect, useState } from 'react'
import type { BlockingModalType } from 'v2/types'
import { PrimaryEvents, primaryEventBus } from 'v2/utils/event-bus/primary'
import { modalConfigs } from './config'

const STORAGE_KEY_PREFIX = 'blocking_modal_'

export const BlockingModal = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [modalType, setModalType] = useState<BlockingModalType | null>(null)
  const [okLoading, setOkLoading] = useState(false)

  useEffect(() => {
    const unsubscribe = primaryEventBus.on(PrimaryEvents.TRIGGER_BLOCKING_MODAL, (type) => {
      const shouldSkip = localStorage.getItem(`${STORAGE_KEY_PREFIX}${type}`) === 'true'

      if (shouldSkip) {
        modalConfigs[type].onPrimaryAction()
        return
      }

      setModalType(type)
      setIsOpen(true)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const handleClose = () => {
    setIsOpen(false)
    setModalType(null)
  }

  const handleConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!modalType) return

    // Get checkbox value directly from the form
    const form = (e.target as HTMLElement).closest('form')
    const checkbox = form?.querySelector<HTMLInputElement>('[name="do-not-show-again"]')

    if (checkbox?.checked) {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${modalType}`, 'true')
    }

    setOkLoading(true)
    modalConfigs[modalType].onPrimaryAction()
    setTimeout(() => {
      handleClose()
      setOkLoading(false)
    }, 2000)
  }

  if (!modalType) return null

  const config = modalConfigs[modalType]

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        {config.showDoNotShowAgain && (
          <div className="flex items-center space-x-2">
            <Checkbox
              name="do-not-show-again"
              label="Do not show this warning again"
              value={false}
              onChange={() => {}} // Checkbox state is read only when needed
              data-testid="checkbox-do-not-show-again"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="button-cancel">
            {config.secondaryAction}
          </Button>
          <ButtonWithLoader
            variant="destructive"
            onClick={handleConfirm}
            data-testid="button-primary-action"
            loading={okLoading}
          >
            {config.primaryAction}
          </ButtonWithLoader>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
